import { cva } from 'class-variance-authority';
import * as React from 'react';
import { DayPicker } from 'react-day-picker';

import { Icons } from '@/assets/icons/icons';
import { cn } from '@/lib/utils';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

// Copy-pasted from shadcn/ui/Button
const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-accent text-foreground hover:bg-accent-hover',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline:
          'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-foreground hover:bg-secondary-hover',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-foreground underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  // Background colors are messy here. bg-transparent is a placeholder.
  // See docs for defaults: https://ui.shadcn.com/docs/components/calendar#installation
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(className)}
      components={{
        Chevron: (props) => {
          if (props.orientation === 'left') {
            return <Icons.chevronLeft {...props} />;
          }
          return <Icons.chevronRight {...props} />;
        },
      }}
      classNames={{
        months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
        month: 'space-y-4',
        month_caption: 'flex justify-center pt-1 relative items-center',
        caption_label: 'text-sm font-medium',
        button_previous: cn(
          buttonVariants({ variant: 'default' }),
          'h-7 w-7 bg-secondary p-0 opacity-50 hover:opacity-100 rounded absolute left-1 hover:bg-secondary-hover'
        ),
        button_next: cn(
          buttonVariants({ variant: 'default' }),
          'h-7 w-7 bg-secondary p-0 opacity-50 hover:opacity-100 rounded absolute right-1 hover:bg-secondary-hover'
        ),
        month_grid: 'w-full border-collapse space-y-1',
        weekdays: 'flex',
        weekday:
          'text-foreground-subtle rounded-md w-9 font-normal text-[0.8rem]',
        week: 'flex w-full mt-2',
        day: 'h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-transparent/50 [&:has([aria-selected])]:bg-transparent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20 rounded-xl',
        day_button: cn(
          'h-8 w-8 p-0 font-normal text-xs aria-selected:opacity-100 rounded-xl',
          'hover:bg-secondary-hover'
        ),
        range_end: 'day-range-end',
        selected:
          'bg-secondary-active text-foreground hover:bg-secondary-hover hover:text-foreground focus:bg-secondary-hover focus:text-foreground rounded-xl',
        today: 'bg-secondary',
        outside:
          'day-outside text-foreground-muted opacity-50 aria-selected:bg-transparent/50 aria-selected:text-foreground-muted aria-selected:opacity-30',
        disabled: 'text-foreground-muted opacity-50',
        range_middle: 'aria-selected:text-foreground',
        hidden: 'invisible',
        ...classNames,
      }}
      {...props}
    />
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
